import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from "@fullcalendar/interaction" // needed for dayClick
import timeGridPlugin from '@fullcalendar/timegrid';
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import { useState, useRef, useEffect } from 'react';
// @mui
import { Card } from 'src/@/components/ui/card';
// redux
import { useDispatch, useLocation, useSelector } from 'src/redux/store';
import { getEvents, openModal, closeModal, updateEvent, selectEvent, selectRange } from 'src/redux/slices/calendar';
// routes
// hooks
import useSettings from 'src/hooks/useSettings';
import useResponsive from 'src/hooks/useResponsive';
// components

// sections
import { CalendarStyle, CalendarToolbar } from 'src/sections/@dashboard/calendar';

import { convertStringArrayToNumber, verifyRules } from 'src/utils/functions';
import useAuth from 'src/hooks/useAuth';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Button } from 'src/@/components/ui/button';
import AddEventDialog from 'src/sections/@dashboard/calendar/CalendarForm';
import { format } from 'date-fns';
import v2_actionCalendarioCriarEvento from 'src/actions/v2/calendario/v2_actionCalendarioCriarEvento';
import Iconify from 'src/components/Iconify';
import v2_actionCalendarioEditarEvento from 'src/actions/v2/calendario/v2_actionCalendarioEditarEvento';
import CalendarSkeleton from 'src/components/skeleton/calendario/CalendarioSkeleton';
import Scrollbar from 'src/components/Scrollbar';
import v2_actionBuscarDadosCliente from 'src/actions/v2/cliente/v2_actionBuscarDadosCliente';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from 'src/@/components/ui/select';
import v2_actionCalendarios from 'src/actions/v2/calendario/v2_actionCalendarios';
import { useNavigate } from 'react-router';
import v2_actionCaledarioDetalhes from 'src/actions/v2/calendario/v2_actionCaledarioDetalhes';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';



// ----------------------------------------------------------------------

const groupEventsByDate = (events) => {
    return events.reduce((groups, event) => {
        const date = event?.start ? format(new Date(event.start), 'yyyy-MM-dd') : 'Unknown Date';
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(event);
        return groups;
    }, {});
};

function renderEventContent(info, isDesktop, events) {
    const event = events.find((event) => event.id === info.event.id);
    let colorText = event.source_event === "lais" ? "text-violet-800" : "text-green-800";
    return (
        <div className={`${event.source_event === "lais" ? "bg-violet-100 border-violet-400" : "bg-green-100 border-green-400"} flex flex-row rounded-sm p-1 gap-2 border h-full`}>
            <div>
                <Iconify icon={event.source_event === "lais" ? "mdi:face-woman-outline" : "flat-color-icons:google"} className="text-xs text-violet-800" />
            </div>
            <div className={`text-[10px]  font-bold ${colorText}`}>
                <span>
                    {new Date(info.event.start).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                    })}
                </span>
                {info.event.end && (
                    <>
                        <span>-</span>
                        <span>
                            {new Date(info.event.end).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </span>
                    </>
                )}
            </div>

            <div className={`text-[10px] font-bold ${colorText}`}> {info?.event?.title.substr(0, (isDesktop ? 15 : 5))}{info?.event?.title?.length > (isDesktop ? 15 : 5) && '...'} </div>

        </div>
    )
}


export default function Calendario() {
    const { user } = useAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const isDesktop = useResponsive('up', 'lg');
    const calendarRef = useRef(null);
    const [date, setDate] = useState(new Date());
    const [view, setView] = useState('timeGridWeek');
    const { events, selectedEventId, isLoading } = useSelector((state) => state.calendar);
    const selectedEvent = useSelector((state) => state.calendar.events.find((event) => event.id === selectedEventId));
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState(['lais']);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [config, setConfig] = useState(null);

    const location = useLocation();
    // Extrai o parâmetro de consulta "channel" da string de busca (search) da URL
    const params = new URLSearchParams(location.search);
    const calenderId = params.get('id');
    const [listCalenders, setListCalenders] = useState([]);
    const [selectCalenderId, setSelectCalenderId] = useState(calenderId);

    const groupedEvents = groupEventsByDate(events);



    // Example usage:
    const numberArray = convertStringArrayToNumber(config?.daysHidden || []);

    const [event, setEvent] = useState({
        id: '',
        title: '',
        description: '',
        startDate: new Date(),
        endDate: new Date(),
        allDay: false,
        source_event: 'lais',
        link: '',
        event_type: 'simple_event',
        time_start: '',
        time_end: ''
    });


    const listCategories = [
        {
            disabled: false,
            value: "lais",
            label: "Lais",
            icon: "mdi:face-woman-outline"
        },
        {
            disabled: true,
            value: "google",
            label: "Google Calendário (em breve)",
            icon: "flat-color-icons:google"
        }
    ]

    function handleChangeValue(event) {
        const { name, value } = event.target;
        setEvent((prev) => ({
            ...prev,
            [name]: value,
        }));
    }


    const handleCheckboxChange = (category) => {
        setSelectedCategories((prev) =>
            prev.includes(category)
                ? prev.filter((item) => item !== category)
                : [...prev, category]
        );
    };


    function handleGetEvents(blockLoading, calendarId) {
        let querys = `?calendar=1&calendar_id=${calendarId ? calendarId : selectCalenderId}`
        dispatch(getEvents(user.clienteId, querys, blockLoading))
    }

    async function buscarConfiguracoes(id) {
        try {
            const response = await v2_actionCaledarioDetalhes(id);
            setConfig(response.data.data.config)
        } catch (error) {
            console.log('error', error)
        }
    }

    async function getListCalenders() {
        try {

            const response = await v2_actionCalendarios(user.clienteId)
            if (response.status === 200) {
                setListCalenders(response.data.data)
                if(response.data.data.length > 0) {
                    console.log('response.data.data[0]._id', response.data.data[0]._id)
                    if(!selectCalenderId) {
                        navigate({
                            pathname: '/dashboard/calendario',
                            search: '?id=' + response.data.data[0]._id
                          })
                        setSelectCalenderId(response.data.data[0]._id)
                    }
             
                }
            }
        } catch (error) {
            console.log(error)
        }
    }



    useEffect(() => {
        buscarConfiguracoes(calenderId);
        getListCalenders();
    }, [calenderId])

    useEffect(() => {
        handleGetEvents(false, calenderId)
    }, [dispatch]);

    useEffect(() => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            const newView = isDesktop ? 'dayGridMonth' : 'timeGridWeek';
            calendarApi.changeView(newView);
            setView(newView);
        }
    }, [isDesktop]);

    const handleClickToday = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.today();
            setDate(calendarApi.getDate());
        }
    };

    const handleChangeView = (newView) => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.changeView(newView);
            setView(newView);
        }
    };

    const handleClickDatePrev = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.prev();
            setDate(calendarApi.getDate());
        }
    };

    const handleClickDateNext = () => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.next();
            setDate(calendarApi.getDate());
        }
    };

    const handleSelectRange = (arg) => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.unselect();
        }
        dispatch(selectRange(arg.start, arg.end));
    };

    const handleSelectEvent = (arg) => {
        dispatch(selectEvent(arg.event?.id));
        setEvent((prev) => ({
            id: arg.event?.id,
            title: arg.event?.title,
            description: arg.event?.description,
            time_start: format(new Date(arg.event.start), 'HH:mm'),
            time_end: format(new Date(arg.event.end), 'HH:mm'),
            allDay: arg.event?.allDay,
            source_event: arg.event?.source_event,
            link: arg.event?.link,
            event_type: arg.event?.event_type,
          

        }));
        setIsEdit(true);
        setDialogOpen(true);
    };



    const handleDropEvent = async ({ event }) => {
        try {
            let scope = {
                id: event.id,
                "start.dateTime": event.start,
                "end.dateTime": event.end,
                time_start: format(new Date(event.start), 'HH:mm'),
                time_end: format(new Date(event.end), 'HH:mm'),
                update_in: new Date()
            }
            handleEdit(scope, true);
        } catch (error) {
            console.error(error);
        }
    };

    const handleEditModal = async () => {
        try {
            let scope = {
                id: event.id,
                "start.dateTime": event.startDate,
                "end.dateTime": event.endDate,
                time_start: format(new Date(event.startDate), 'HH:mm'),
                time_end: format(new Date(event.endDate), 'HH:mm'),
                update_in: new Date(),
                summary: event.title,
                description: event.description,
                event_type: event.event_type,
                allDay: event.allDay,
                // source: event.source || 'lais',
                link: event.link
            }
            await handleEdit(scope);
            // handleGetEvents();
        } catch (error) {
            console.error(error);
        }
    }

    function handleClose() {
        setEvent(
            {
                id: '',
                title: '',
                description: '',
                startDate: new Date(),
                endDate: new Date(),
                allDay: false,
                source_event: 'lais',
                link: '',
                event_type: 'simple_event'
            }
        )

        setDialogOpen(!dialogOpen);
        setIsEdit(false);
    }

    async function handleCreate() {

        let dataCreate = {
            "event_type": event.event_type,
            "description": event.description,
            "summary": event.title,
            "event_id": "",
            "status": "",
            "creator_email": "",
            "location": "",
            "is_meeting": false,
            "user_id": user.id,
            "attendees": [],
            "meeting_link": "",
            "cliente_id": user.clienteId,
            "startDate": format(event.startDate, "yyyy-MM-dd"),
            "endDate": format(event.endDate, "yyyy-MM-dd"),
            "time_start": event.time_start,
            "time_end": event.time_end,
            "source_event": event.source_event,
            calendar_id: calenderId
        }

        let data = {
            scope: dataCreate
        }

        try {
            await v2_actionCalendarioCriarEvento(user.clienteId, data);
            handleGetEvents(true, calenderId);
            setEvent(
                {
                    id: '',
                    title: '',
                    description: '',
                    startDate: new Date(),
                    endDate: new Date(),
                    allDay: false,
                    source_event: 'lais',
                    link: '',
                    event_type: 'simple_event',
                    time_start: '',
                    time_end: '',
                    calendar_id: calenderId
                }
            )
        } catch (error) {
            console.log(error);
        }

    }

    const handleEdit = async (scopeData, blockLoading) => {
        try {
            let data = {
                scope: scopeData
            }
            await v2_actionCalendarioEditarEvento(scopeData.id, data);
            handleGetEvents(blockLoading, calenderId);
        } catch (error) {
            console.log(error);
        }
    }


    return (
        isLoading ? <CalendarSkeleton /> :
            <div className="p-4 flex flex-row bg-slate-50 h-screen">
   
                <div className='w-1/6 flex flex-col p-4 gap-8 rounded-md'>
                    <Select 
                         value={selectCalenderId} 
                         onValueChange={(e) => {
                          console.log('e', e)
                           navigate({
                            pathname: '/dashboard/calendario',
                            search: '?id=' + e
                          })
                          setSelectCalenderId(e)
                          handleGetEvents(true, e)
                          buscarConfiguracoes(e);
                    }}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder={selectCalenderId ? selectCalenderId : "Selecione..."} />
                            <SelectContent>
                                {listCalenders.map((option) => (
                                    <SelectItem key={option._id} value={option._id}>
                                        {option.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </SelectTrigger>
                    </Select>

                    <AddEventDialog
                        user={user}
                        open={dialogOpen}
                        onOpenChange={() => {
                            setDialogOpen(!dialogOpen)
                            setIsEdit(false)
                        }}
                        onClose={handleClose}
                        event={event}
                        onEdit={handleEditModal}
                        onCreate={handleCreate}
                        changeValue={handleChangeValue}
                        onCallback={handleGetEvents}
                        isEdit={isEdit}
                    />


                    <div className="col-span-1">
                        <h2 className="text-sm font-semibold mb-4">Categorias</h2>
                        <ul className="space-y-2">
                            {listCategories.map((category) => (
                                <li key={category.value}>

                                    <label className="flex items-center space-x-2">

                                        <Checkbox
                                            disabled={category.disabled}
                                            checked={selectedCategories.includes(category.value)}
                                            onCheckedChange={() => handleCheckboxChange(category.value)}
                                        />

                                        <span className='text-sm'>{category.label}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Upcoming Events */}
                    <div className="col-span-1 mt-4">
                        <h2 className="text-sm font-semibold mb-4">Próximos Eventos</h2>
                        <Scrollbar className="h-[500px]">
                            <div className="space-y-4">
                                {Object.keys(groupedEvents).map((date) => (
                                    <div key={date}>
                                        <div className="text-gray-500 font-normal text-[11px] mb-2">
                                            {format(new Date(date), 'dd MMM yyyy')}
                                        </div>

                                        <div className="grid grid-cols-1 space-y-1">
                                            {groupedEvents[date].map((event) => (
                                                <div
                                                    onClick={() => {
                                                        setEvent({
                                                            id: event?.id,
                                                            title: event?.title,
                                                            description: event?.description,
                                                            startDate: event?.start.dateTime,
                                                            endDate: event?.end.dateTime,
                                                            allDay: event?.allDay,
                                                            source_event: event?.source_event,
                                                            link: event?.link,
                                                            event_type: event?.event_type,
                                                            time_start: event?.time_start,
                                                            time_end: event?.time_end,
                                                        });
                                                        setDialogOpen(true);
                                                    }}
                                                    key={event.id}
                                                    className="flex bg-white rounded p-2 flex-row items-center shadow-xs cursor-pointer border hover:bg-violet-50 hover:border-violet-200 transition"
                                                >
                                                    <div>
                                                        <span className="text-violet-900 text-xs">
                                                            {event.time_start} - {event.time_end}
                                                        </span>
                                                        <div className="text-sm font-semibold">
                                                            {event.title}
                                                        </div>
                                                    </div>


                                                </div>
                                            ))}
                                        </div>

                                    </div>
                                ))}
                            </div>
                        </Scrollbar>
                    </div>

                    {/* Categories */}


                </div>
                <div className='w-full'>
                    <Card>
                        <CalendarToolbar
                            date={date}
                            view={view}
                            onNextDate={handleClickDateNext}
                            onPrevDate={handleClickDatePrev}
                            onToday={handleClickToday}
                            onChangeView={handleChangeView}
                        />
                        <CalendarStyle>
                            <FullCalendar
                                hiddenDays={numberArray}
                                plugins={[
                                    dayGridPlugin,
                                    timeGridPlugin,
                                    interactionPlugin
                                ]}
                                weekends
                                slotMinTime={config?.startTime || '00:00:00'}
                                slotMaxTime={config?.endTime || '23:59:59'}
                                slotDuration={config?.slotDuration || '00:30:00'}
                                editable
                                droppable
                                selectable
                                eventOverlap
                                allDaySlot
                                events={events}
                                firstDay={1}
                                ref={calendarRef}
                                rerenderDelay={10}
                                initialDate={date}
                                initialView={view}
                                dayMaxEventRows={1}
                                dayMaxEvents={true}
                                eventDisplay="block"
                                headerToolbar={false}
                                allDayMaintainDuration
                                eventResizableFromStart
                                //eventAdd={event => {}}
                                select={value => {
                                    console.log('value', value)
                                    setEvent(
                                        {
                                            id: '',
                                            title: '',
                                            description: '',
                                            startDate: value.start,
                                            endDate: value.end,
                                            allDay: value.allDay,
                                            time_start: format(new Date(value.start), 'HH:mm'),
                                            time_end: format(new Date(value.end), 'HH:mm'),
                                            source_event: 'lais',
                                            link: '',
                                            event_type: 'simple_event'
                                        }
                                    )
                                    setDialogOpen(true);
                                }}
                                eventDrop={handleDropEvent}
                                eventClick={handleSelectEvent}
                                eventResize={handleDropEvent}
                                eventContent={(info, event) => (
                                    renderEventContent(info, isDesktop, events)
                                )}
                                height={isDesktop ? 720 : 'auto'}
                                locale={ptBrLocale}
                            />
                        </CalendarStyle>
                    </Card>
                </div>

                {/* <EventDialog
            open={dialogOpen}
            selected={selectedCell}
            submit={handleSubmit}
            onDialogOpen={setDialogOpen}
        /> */}
            </div>
    )


}
